import { Navigate, Routes as RouterRoutes, useLocation } from 'react-router-dom'

import { AgentVerifyContainer } from '../agentVerify/AgentVerifyContainer'
import { AccountVerification } from '../auth/accountVerification/AccountVerification'

import { EmailVerificationPage } from '../auth/emailVerification/EmailVerificationPage'
import { ForgotPassword } from '../auth/forgotPassword/ForgotPassword'
import { LoginPage } from '../auth/login/LoginPage'
import { SetPassword } from '../auth/setPassword/SetPassword'
import { ProfilePage } from '../causeProfile/ProfilePage'
import { DonationsPage } from '../donations/DonationsPage'
import { MyAccountContainer } from '../myAccount/MyAccountContainer'
import { PayoutsPage } from '../payouts/PayoutsPage'
import { ProductOffersPage } from '../productOffers/ProductOffersPage'
import { AgentVerificationEmailValidation } from '@percent/cause-dashboard/app/auth/agentVerification/AgentVerificationEmailValidation'
import { ExpiredSessionHandleContainer } from '@percent/cause-dashboard/app/auth/expiredSessionHandleContainer/ExpiredSessionHandleContainer'
import { LoginWithRecoveryCodeContainer } from '@percent/cause-dashboard/app/auth/login/TwoFactorAuthentication/LoginWithRecoveryCode/LoginWithRecoveryCodeContainer'
import { LoginWithTwoFA } from '@percent/cause-dashboard/app/auth/login/TwoFactorAuthentication/LoginWithTwoFA/LoginWithTwoFA'
import { TwoFactorAuthenticationSetup } from '@percent/cause-dashboard/app/auth/login/TwoFactorAuthentication/TwoFactorAuthenticationSetup'
import { UserState } from '@percent/cause-dashboard/common/components/route/AuthRoute'
import { POSTHOG_FEATURE_FLAGS, useAuthState, useIsStatusCheckSignUp } from '@percent/cause-dashboard/common/hooks'
import { selectAuthState } from '@percent/cause-dashboard/context/auth'
import { createAuthRoute } from './routeHelpers'
import { AgentVerificationDocumentsContainer } from '../agentVerificationDocuments/AgentVerificationDocumentsContainer'
import { ValidationRequestDocumentsContainer } from '../validationRequestDocuments/ValidationRequestDocumentsContainer'
import { AcceptInvitePage } from '../auth/acceptEmailInvitation/AcceptInvitePage'
import { RequestToJoinOrgForm } from '../auth/claim/RequestToJoinOrganisation/RequestToJoinOrganisationForm/RequestToJoinOrgForm'
import { MultiStepAddOrganization } from '../auth/multiStepAddOrganization/MultiStepAddOrganization'
import { DashboardLayout } from '@percent/cause-dashboard/app/layout/dashboardLayout/DashboardLayout'
import { PayoutDetailsPage } from '../payouts/PayoutDetailsPage/PayoutDetailsPage'
import { UsersPage } from '../users/UsersPage'
import { ClaimPage } from '../auth/claim/ClaimPage'
import { HomePage } from '@percent/cause-dashboard/app/home/HomePage'
import { OneStepSignUp } from '../auth/oneStepSignUp/OneStepSignUp'
import { SignUpCheckEmail } from '../auth/signUpCheckEmail/SignUpCheckEmail'
import { GrantsPage } from '../grants/GrantsPage'
import { MyGrantDetailsPage } from '../grants/grantDetailsPage/grantDetailsPage'
import { AIGateway } from '../ai/AIGateway'
import { MyDiscounts } from '../myDiscounts/MyDiscounts'
import { SavingsCalculatorPage } from '../savingsCalculator/SavingsCalculatorPage'
import { DonationFormContainer } from '../donationForm/DonationFormContainer'
import { Volunteering } from '../volunteering/Volunteering'
import { CreateOpportunityForm } from '../volunteering/opportunities/create/CreateOpportunityForm'
import { EditOpportunityForm } from '../volunteering/opportunities/edit/EditOpportunityForm'
import { ProductOfferDetailCMSPage } from '@percent/cause-dashboard/app/productOffersDetail/ProductOfferDetailCMSPage'
import { DiscountRedemptionSurveyPage } from '../surveys/discountRedemption/DiscountRedemptionSurveyPage'
import { AgentVerificationKycResultPage } from '../agentVerificationKycResult/AgentVerificationKycResultPage'
import { ThanksForYourInterestPage } from '../thanksForYourInterest/ThanksForYourInterestPage'

export enum RoutePath {
  AI_GATEWAY = '/ai',
  MY_DISCOUNTS = '/my-discounts',
  SIGNIN_2FA_RECOVERY = '/signin/2fa-recovery',
  SIGNIN_2FA = '/signin/2fa',
  SIGNIN_2FA_SETUP = '/signin/2fa-setup',
  SIGNIN = '/signin',
  EXPIRED_SESSION = '/expired-session',
  AGENT_VERIFICATION = '/agent-verification',
  AGENT_VERIFICATION_DOCUMENTS = '/agent-verification-documents',
  VALIDATION_REQUEST_DOCUMENTS = '/validation-request-documents',
  SIGNUP = '/signup',
  SIGNUP_CHECK_EMAIL = '/signup/check-email',
  FORGOT_PASSWORD = '/forgot-password',
  SET_PASSWORD = '/set-password',
  ACCOUNT_VERIFICATION = '/account-verification',
  HOME = '/home',
  PRODUCT_OFFERS = '/product-offers',
  PRODUCT_OFFERS_DETAIL_CMS = '/product-offers/:offerDetailSlug',
  SAVINGS_CALCULATOR = '/savings-calculator',
  PROFILE = '/profile',
  USERS = '/users',
  DONATION_FORM = '/donation-form',
  DONATIONS = '/donations',
  GRANTS = '/grants',
  GRANTS_DETAILS = '/grants/:candidateId/:tab?',
  PAYOUTS = '/payouts',
  PAYOUT = '/payouts/:id',
  MY_ACCOUNT = '/my-account',
  CLAIM = '/claim',
  AGENT_VERIFY = '/agent-verify',
  ADD_ORGANIZATION = '/add-organization',
  POST_VERIFICATION_SURVEY = '/post-verification-survey',
  EMAIL_VERIFICATION = '/email-verification',
  ACCEPT_INVITE = '/accept-invite',
  REQUEST_TO_JOIN = '/request-to-join',
  VOLUNTEERING = '/volunteering',
  VOLUNTEERING_CREATE_OPPORTUNITY = '/volunteering/opportunities/create',
  VOLUNTEERING_EDIT_OPPORTUNITY = '/volunteering/opportunities/:id/edit',
  DISCOUNT_REDEMPTION_SURVEY = '/surveys/discount-redemptions/:token',
  AGENT_VERIFICATION_KYC_RESULT = '/agent-verification-kyc-result',
  THANKS_FOR_YOUR_INTEREST = '/thanks-for-your-interest'
}

const OrgIdParamRouteWrapper = () => {
  const { search } = useLocation()
  const urlQuery = new URLSearchParams(search)
  const orgIdParam = urlQuery.get('organization-id')

  if (orgIdParam) {
    return <OneStepSignUp />
  }

  return <Navigate to="/" replace />
}

const ProfileRouteWrapper = () => {
  const isStatusCheckSignUp = useIsStatusCheckSignUp()
  const { authState } = useAuthState()
  const { claimAccepted } = selectAuthState(authState)

  if (isStatusCheckSignUp && !claimAccepted) {
    return <Navigate to={RoutePath.HOME} replace />
  }

  return (
    <DashboardLayout>
      <ProfilePage />
    </DashboardLayout>
  )
}

export function Routes() {
  return (
    <RouterRoutes>
      {createAuthRoute(RoutePath.SIGNIN_2FA_RECOVERY, {
        allow: [UserState.LoggedOut],
        component: LoginWithRecoveryCodeContainer
      })}
      {createAuthRoute(RoutePath.SIGNIN_2FA, {
        allow: [UserState.LoggedOut],
        component: LoginWithTwoFA
      })}
      {createAuthRoute(RoutePath.SIGNIN_2FA_SETUP, {
        allow: [UserState.Any],
        component: TwoFactorAuthenticationSetup
      })}
      {createAuthRoute(RoutePath.SIGNIN, {
        allow: [UserState.LoggedOut],
        component: LoginPage
      })}
      {createAuthRoute(RoutePath.EXPIRED_SESSION, {
        allow: [UserState.LoggedOut],
        component: ExpiredSessionHandleContainer
      })}
      {createAuthRoute(RoutePath.AGENT_VERIFICATION, {
        allow: [UserState.Any],
        component: AgentVerificationEmailValidation
      })}
      {createAuthRoute(RoutePath.AGENT_VERIFICATION_DOCUMENTS, {
        allow: [UserState.Any],
        component: AgentVerificationDocumentsContainer
      })}
      {createAuthRoute(RoutePath.VALIDATION_REQUEST_DOCUMENTS, {
        allow: [UserState.Any],
        component: ValidationRequestDocumentsContainer
      })}
      {createAuthRoute(RoutePath.SIGNUP, {
        allow: [UserState.LoggedOut],
        component: OrgIdParamRouteWrapper
      })}
      {createAuthRoute(RoutePath.SIGNUP_CHECK_EMAIL, {
        allow: [UserState.LoggedOut],
        component: SignUpCheckEmail
      })}
      {createAuthRoute(RoutePath.ADD_ORGANIZATION, {
        disallow: [UserState.LoggedInClaimMade],
        allow: [UserState.LoggedOut, UserState.LoggedIn],
        component: MultiStepAddOrganization
      })}
      {createAuthRoute(RoutePath.FORGOT_PASSWORD, {
        allow: [UserState.LoggedOut],
        component: ForgotPassword
      })}
      {createAuthRoute(RoutePath.SET_PASSWORD, {
        allow: [UserState.LoggedOut],
        component: SetPassword
      })}
      {createAuthRoute(RoutePath.ACCOUNT_VERIFICATION, {
        allow: [UserState.LoggedOut, UserState.LoggedInUnverified],
        component: AccountVerification
      })}
      {createAuthRoute(RoutePath.PRODUCT_OFFERS_DETAIL_CMS, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <ProductOfferDetailCMSPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.PRODUCT_OFFERS, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <ProductOffersPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.HOME, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <HomePage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.PROFILE, {
        allow: [UserState.LoggedInClaimMade],
        children: <ProfileRouteWrapper />
      })}
      {createAuthRoute(RoutePath.USERS, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <UsersPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.DONATION_FORM, {
        allow: [UserState.LoggedInClaimMade],
        feature: POSTHOG_FEATURE_FLAGS.CP_DONATION_FORM_APPLICATION,
        children: (
          <DashboardLayout>
            <DonationFormContainer />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.DONATIONS, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <DonationsPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.AI_GATEWAY, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <AIGateway />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.SAVINGS_CALCULATOR, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <SavingsCalculatorPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.MY_DISCOUNTS, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <MyDiscounts />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.VOLUNTEERING, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <Volunteering />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.VOLUNTEERING_CREATE_OPPORTUNITY, {
        allow: [UserState.LoggedInClaimMade],
        children: <CreateOpportunityForm />
      })}
      {createAuthRoute(RoutePath.VOLUNTEERING_EDIT_OPPORTUNITY, {
        allow: [UserState.LoggedInClaimMade],
        children: <EditOpportunityForm />
      })}
      {createAuthRoute(RoutePath.GRANTS, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <GrantsPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.GRANTS_DETAILS, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <MyGrantDetailsPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.PAYOUTS, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <PayoutsPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.PAYOUT, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <PayoutDetailsPage />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.MY_ACCOUNT, {
        allow: [UserState.LoggedInClaimMade],
        children: (
          <DashboardLayout>
            <MyAccountContainer />
          </DashboardLayout>
        )
      })}
      {createAuthRoute(RoutePath.CLAIM, {
        disallow: [UserState.LoggedInClaimMade],
        allow: [UserState.LoggedOut, UserState.LoggedIn],
        component: ClaimPage
      })}
      {createAuthRoute(RoutePath.AGENT_VERIFY, {
        allow: [UserState.Any],
        component: AgentVerifyContainer
      })}
      {createAuthRoute(RoutePath.EMAIL_VERIFICATION, {
        allow: [UserState.LoggedInUnverified],
        component: EmailVerificationPage
      })}
      {createAuthRoute(RoutePath.ACCEPT_INVITE, {
        allow: [UserState.Any],
        component: AcceptInvitePage
      })}
      {createAuthRoute(RoutePath.REQUEST_TO_JOIN, {
        allow: [UserState.Any],
        component: RequestToJoinOrgForm
      })}
      {createAuthRoute(RoutePath.DISCOUNT_REDEMPTION_SURVEY, {
        allow: [UserState.Any],
        component: DiscountRedemptionSurveyPage
      })}
      {createAuthRoute(RoutePath.AGENT_VERIFICATION_KYC_RESULT, {
        allow: [UserState.Any],
        component: AgentVerificationKycResultPage
      })}
      {createAuthRoute(RoutePath.THANKS_FOR_YOUR_INTEREST, {
        allow: [UserState.Any],
        component: ThanksForYourInterestPage
      })}
      {createAuthRoute('/', {
        disallow: [UserState.LoggedInClaimMade],
        allow: [UserState.LoggedOut, UserState.LoggedIn],
        component: ClaimPage
      })}
      {createAuthRoute('*', {
        allow: [UserState.Any]
      })}
    </RouterRoutes>
  )
}
